/**
 * Work windows — the rules behind the hour picker.
 *
 * Mirrors `packages/api/src/modules/linkedin/work-block.service.ts`: up to two
 * contiguous windows per day, each 1-3 hours, together at most 5 hours. The
 * storage format stays a flat hour list (`[9,10,11,14,15]` = 9-12 and 14-16).
 *
 * The UI must never offer a selection the server rejects, so both sides compute
 * the same thing — keep this file in sync with the service.
 */

export const MIN_BLOCK_HOURS = 1;
export const MAX_BLOCK_HOURS = 3;
export const MAX_BLOCKS_PER_DAY = 2;
export const MAX_DAILY_WORK_HOURS = 5;

export interface WorkBlock {
  startHour: number;
  durationHours: number;
  hours: number[];
}

export type WorkHoursViolation = 'invalidHour' | 'maxDailyHours' | 'maxBlocks' | 'maxBlockLength';

/** Group an hour list into its contiguous windows (sorted, de-duplicated). */
export function toWorkBlocks(workHours: number[]): WorkBlock[] {
  const hours = [...new Set((workHours || []).filter((h) => Number.isInteger(h) && h >= 0 && h <= 23))].sort(
    (a, b) => a - b
  );
  const blocks: WorkBlock[] = [];
  for (const hour of hours) {
    const last = blocks[blocks.length - 1];
    if (last && hour === last.startHour + last.durationHours) {
      last.durationHours += 1;
      last.hours.push(hour);
    } else {
      blocks.push({ startHour: hour, durationHours: 1, hours: [hour] });
    }
  }
  return blocks;
}

/** An empty list is valid: it means "not configured", not "illegal". */
export function validateWorkHours(workHours: number[]): { valid: boolean; violation?: WorkHoursViolation } {
  if (workHours.some((h) => !Number.isInteger(h) || h < 0 || h > 23)) {
    return { valid: false, violation: 'invalidHour' };
  }
  const hours = [...new Set(workHours)];
  if (hours.length === 0) return { valid: true };
  if (hours.length > MAX_DAILY_WORK_HOURS) return { valid: false, violation: 'maxDailyHours' };
  const blocks = toWorkBlocks(hours);
  if (blocks.length > MAX_BLOCKS_PER_DAY) return { valid: false, violation: 'maxBlocks' };
  if (blocks.some((b) => b.durationHours > MAX_BLOCK_HOURS)) return { valid: false, violation: 'maxBlockLength' };
  return { valid: true };
}

/**
 * Trim a legacy selection down to the current rules — keep the earliest windows,
 * shorten each to MAX_BLOCK_HOURS, stop at the daily ceiling.
 * Accounts still carry the old free-form default [9,10,11,14,15,16] (6h).
 */
export function normalizeWorkHours(workHours: number[]): number[] {
  const kept: number[] = [];
  for (const block of toWorkBlocks(workHours).slice(0, MAX_BLOCKS_PER_DAY)) {
    for (const hour of block.hours.slice(0, MAX_BLOCK_HOURS)) {
      if (kept.length >= MAX_DAILY_WORK_HOURS) return kept;
      kept.push(hour);
    }
  }
  return kept;
}

export interface HourState {
  active: boolean;
  /** false → render the hour greyed out and explain why via `violation` */
  selectable: boolean;
  violation?: WorkHoursViolation;
}

/**
 * What a single hour button may do right now. An already-selected hour is always
 * clickable (shrinking is never blocked); a free hour is only offered when adding
 * it keeps the whole selection legal.
 */
export function getHourState(workHours: number[], hour: number): HourState {
  if (workHours.includes(hour)) return { active: true, selectable: true };
  const result = validateWorkHours([...workHours, hour]);
  return { active: false, selectable: result.valid, violation: result.violation };
}

/**
 * Click behaviour:
 * - free hour → add it (extends a neighbouring window or opens a second one)
 * - selected hour → cut its window back to just before it, so a click always
 *   shortens and never leaves a third window behind
 */
export function toggleWorkHour(workHours: number[], hour: number): number[] {
  if (workHours.includes(hour)) {
    const block = toWorkBlocks(workHours).find((b) => b.hours.includes(hour));
    const tail = block ? block.hours.filter((h) => h >= hour) : [hour];
    return workHours.filter((h) => !tail.includes(h)).sort((a, b) => a - b);
  }
  if (!getHourState(workHours, hour).selectable) return workHours;
  return [...workHours, hour].sort((a, b) => a - b);
}

/** "9–12" for a window starting at 9 and lasting 3 hours. */
export function formatBlockRange(block: WorkBlock): string {
  return `${block.startHour}–${block.startHour + block.durationHours}`;
}
